<script setup lang="ts">
import { usePage } from '@inertiajs/vue3'
import { computed, nextTick, ref, watch } from 'vue'
import { visitLink } from '@/lib/visitLink'

const open = defineModel<boolean>({ required: true })

const items = [
  { title: 'Inicio', href: '/', icon: 'mdi-home' },
  { title: 'Presupuestos', href: '/quotes', icon: 'mdi-file-document-multiple' },
  { title: 'Clientes', href: '/clients', icon: 'mdi-account-group' },
  { title: 'Mi negocio', href: '/business/edit', icon: 'mdi-store' },
]

const page = usePage()
const path = computed(() => page.url.split(/[?#]/)[0])
const list = ref()

// A section stays current on its sub-pages (/quotes/1); "Inicio" only on the root.
function isCurrent(href: string) {
  return href === '/' ? path.value === '/' : path.value === href || path.value.startsWith(`${href}/`)
}

function visit(event: MouseEvent | KeyboardEvent, href: string) {
  if (visitLink(event, href)) open.value = false
}

// Vuetify's drawer keeps Tab inside once focus is there, but doesn't move it in on open.
watch(open, async (value) => {
  if (!value) return
  await nextTick()
  list.value?.$el.querySelector('a')?.focus()
})
</script>

<template>
  <!-- Items get tabindex 0: by default a v-list is a single Tab stop (items move with the arrows),
       which leaves retain-focus nothing to cycle through, so Tab escaped to the page behind. -->
  <v-navigation-drawer
    id="app-navigation"
    v-model="open"
    temporary
    retain-focus
    @keydown.esc="open = false"
  >
    <v-list ref="list" nav>
      <v-list-subheader>Menú</v-list-subheader>

      <v-list-item
        v-for="item in items"
        :key="item.href"
        :href="item.href"
        :prepend-icon="item.icon"
        :title="item.title"
        :active="isCurrent(item.href)"
        :aria-current="isCurrent(item.href) ? 'page' : undefined"
        color="primary"
        rounded="lg"
        tabindex="0"
        @click="visit($event, item.href)"
      />
    </v-list>
  </v-navigation-drawer>
</template>

<style scoped>
/* Icons take the color of the text they go with (design system, Iconografía). */
.v-list-item :deep(.v-list-item__prepend > .v-icon) {
  opacity: 1;
}
</style>
